import { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import { taskNavForWorkspace } from './taskNav';
import { isLimitedWorkspace } from '../utils/limitedAccess';
import { useSharedMeetlyCount } from '../hrms/hooks/useSharedMeetlyCount';
import { MEETLY_SHARED_PATH, meetlyNavHref } from '../hrms/utils/meetlyShare';

/**
 * TaskNavPanel — the Task Management section's list panel.
 *
 * Renders beside the fixed icon rail (AppSidebar) while the user is on a Task
 * route, mirroring how the Chat conversation list opens beside the rail. It
 * reuses the `.gt-sidebar` styling (card, items, active states, dark-mode and
 * the mobile `display:none` rule) so it needs no bespoke CSS and stays visually
 * consistent with the rail. Purely navigation — the draft badge lives on the
 * rail's Tasks icon (globally visible) so it isn't duplicated here.
 *
 * Width is user-adjustable: drag the right-edge handle to resize. The chosen
 * width is clamped to [MIN_WIDTH, MAX_WIDTH] and persisted to localStorage so it
 * sticks across sessions.
 */
const MIN_WIDTH = 168;
const MAX_WIDTH = 320;
const DEFAULT_WIDTH = 188;
const STORAGE_KEY = 'teamlio:taskNavWidth';

function readStoredWidth(): number {
  try {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(saved) && saved >= MIN_WIDTH && saved <= MAX_WIDTH) return saved;
  } catch {
    /* localStorage unavailable — fall back to the default */
  }
  return DEFAULT_WIDTH;
}

export default function TaskNavPanel() {
  const { user } = useAuth();
  const { hasAccess, isPathVisible } = useAccess();
  // Meeting for a colleague without Meetly: only there to reach "Shared with me".
  const meetlyVisible = !hasAccess || isPathVisible('/meetly');
  const meetlyHref = meetlyNavHref(meetlyVisible, useSharedMeetlyCount(!meetlyVisible));
  const [width, setWidth] = useState<number>(readStoredWidth);
  const draggingRef = useRef(false);

  const items = taskNavForWorkspace(isLimitedWorkspace(user?.subscription))
    .flatMap((c) => (c.to === '/meetly' ? (meetlyHref ? [{ ...c, to: meetlyHref }] : []) : [c]))
    .filter((c) => !hasAccess || c.to === MEETLY_SHARED_PATH || isPathVisible(c.to));
  if (items.length === 0) return null;

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    if (draggingRef.current) return;
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = width;
    let finalW = startW;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      finalW = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + (ev.clientX - startX)));
      setWidth(finalW);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try {
        localStorage.setItem(STORAGE_KEY, String(Math.round(finalW)));
      } catch {
        /* ignore persistence failures */
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Double-click the handle to snap back to the default width.
  const resetWidth = () => {
    setWidth(DEFAULT_WIDTH);
    try {
      localStorage.setItem(STORAGE_KEY, String(DEFAULT_WIDTH));
    } catch {
      /* ignore */
    }
  };

  return (
    <aside
      className="gt-sidebar gt-task-panel"
      style={{ width, flexShrink: 0, position: 'relative' }}
    >
      <div className="gt-sidebar-header">
        <span className="gt-sidebar-brand">Tasks</span>
      </div>

      <nav className="gt-sidebar-nav">
        {items.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `gt-sidebar-item${isActive ? ' is-active' : ''}`}
          >
            <Icon className="gt-sidebar-icon" />
            <span className="gt-sidebar-label">{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Right-edge resize handle: drag to adjust width, double-click to reset. */}
      <div
        className="gt-task-panel__resize"
        onMouseDown={startResize}
        onDoubleClick={resetWidth}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize task panel"
        title="Drag to resize · double-click to reset"
        style={{
          position: 'absolute',
          top: 0,
          right: -3,
          width: 7,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 5,
        }}
      />
    </aside>
  );
}
